import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight, Shield, Heart, Eye, Users } from "lucide-react";
import EditableImage from "@/components/EditableImage";
import { getAuthHeaders } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function About() {
  const [settings, setSettings] = useState(null);

  const fetchSettings = useCallback(() => {
    axios.get(`${API}/site-settings`).then(res => setSettings(res.data)).catch(() => {});
  }, []);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  const aboutDetailImage = settings?.about_detail_image || "https://images.unsplash.com/photo-1749704648393-d0916fd839fc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHw0fHxsdXh1cnklMjBsaXZpbmclMjByb29tJTIwcG9vbCUyMHZpZXd8ZW58MHx8fHwxNzc2MzA3OTU3fDA&ixlib=rb-4.1.0&q=85";

  const updateSetting = async (key, url) => {
    const updated = { ...settings, [key]: url };
    await axios.put(`${API}/admin/site-settings`, updated, { headers: getAuthHeaders(), withCredentials: true });
    setSettings(updated);
  };

  return (
    <div data-testid="about-page" className="bg-white pt-20">
      {/* Hero */}
      <section className="relative px-6 md:px-12 lg:px-24 py-24">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-7">
            <p className="text-xs uppercase tracking-[0.3em] text-[#32A334] mb-4">Our Story</p>
            <h1 className="font-['DM_Serif_Display'] text-5xl sm:text-6xl tracking-tight text-[#1A1A2E] mb-8 leading-none">
              Exceptional Hospitality,<br />Every Stay.
            </h1>
            <p className="text-[#64748B] text-base leading-relaxed mb-6 max-w-lg">
              K10 Properties Group was founded with a singular vision: to redefine short-term rental hospitality. We believe every stay should feel extraordinary, combining the comfort of home with the luxury of a five-star experience.
            </p>
            <p className="text-[#64748B] text-base leading-relaxed max-w-lg">
              Our team personally selects each property in our portfolio, ensuring it meets our rigorous standards for quality, location, and guest experience. From mountain retreats to lakeside escapes, every K10 property tells a unique story.
            </p>
          </div>
          <div className="md:col-span-5">
            <div className="relative aspect-[3/4] overflow-hidden rounded-lg">
              <EditableImage src={aboutDetailImage} alt="Luxury property" onReplace={(url) => updateSetting("about_detail_image", url)}>
                <img src={aboutDetailImage} alt="Luxury property" className="w-full h-full object-cover" />
              </EditableImage>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="px-6 md:px-12 lg:px-24 py-24 bg-[#F4F6FA]">
        <div className="text-center mb-16">
          <p className="text-xs uppercase tracking-[0.3em] text-[#32A334] mb-3">Our Values</p>
          <h2 className="font-['DM_Serif_Display'] text-4xl sm:text-5xl tracking-tight text-[#1A1A2E]">What Drives Us</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {[
            { icon: Eye, title: "Attention to Detail", desc: "Every element of our properties is considered, from thread counts to appliance quality." },
            { icon: Heart, title: "Genuine Care", desc: "We treat every guest like family, anticipating needs before they arise." },
            { icon: Shield, title: "Trust & Transparency", desc: "What you see is what you get. Honest listings, clear communication, no surprises." },
            { icon: Users, title: "Community", desc: "We support local communities and partner with local businesses wherever possible." },
          ].map((v, i) => (
            <div key={i} className="p-8 bg-white border border-[#E2E8F0] rounded-lg hover:shadow-md transition-all duration-500">
              <v.icon className="w-8 h-8 text-[#32A334] mb-6" strokeWidth={1.5} />
              <h3 className="font-['DM_Serif_Display'] text-xl font-semibold text-[#1A1A2E] mb-3">{v.title}</h3>
              <p className="text-[#64748B] text-sm leading-relaxed">{v.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="px-6 md:px-12 lg:px-24 py-24 text-center">
        <h2 className="font-['DM_Serif_Display'] text-4xl sm:text-5xl tracking-tight text-[#1A1A2E] mb-8">
          Ready to Experience<br />the K10 Difference?
        </h2>
        <div className="flex justify-center gap-4 flex-wrap">
          <Link
            to="/properties"
            data-testid="about-cta-properties"
            className="bg-[#1C671D] text-white hover:bg-[#145215] transition-colors duration-300 px-8 py-3.5 uppercase tracking-widest text-sm inline-flex items-center gap-3 rounded"
          >
            Browse Properties <ArrowRight className="w-4 h-4" />
          </Link>
          <Link
            to="/contact"
            data-testid="about-cta-contact"
            className="border border-[#1A1A2E] text-[#1A1A2E] hover:bg-[#1A1A2E] hover:text-white transition-colors duration-300 px-8 py-3.5 uppercase tracking-widest text-sm rounded"
          >
            Get In Touch
          </Link>
        </div>
      </section>
    </div>
  );
}
